import { useSuspenseQuery } from '@apollo/client';
import { Suspense, useCallback, type FC } from 'react';
import { GET_DEVICE_NAME } from './query';
import { Skeleton, Typography, type TypographyProps } from '@mui/material';
import ApolloErrorBoundary from '@components/ApolloErrorBoundary';

interface Props {
  deviceId: string;
  /**
   * Ante un error muestra el id en línea en vez de la alerta de sección, que rompe un contenedor
   * de alto fijo (p. ej. las filas del banner de movimientos).
   */
  inlineError?: boolean;
}

const Component: FC<Props & TypographyProps> = ({ deviceId, inlineError: _, ...typographyProps }) => {
  const { data } = useSuspenseQuery(GET_DEVICE_NAME, { variables: { deviceId } });
  return (
    <Typography variant="inherit" component="span" {...typographyProps}>
      {data.device.profile.name}
    </Typography>
  );
};

const Fallback: FC<TypographyProps> = (props) => {
  return (
    <Skeleton variant="text" sx={{ display: 'inline', ...props }}>
      <Typography variant="inherit" component="span" {...props}>
        placeholder
      </Typography>
    </Skeleton>
  );
};

const DeviceNameSpan: FC<Props & TypographyProps> = (props) => {
  const { deviceId, inlineError, ...typographyProps } = props;
  const InlineFallback = useCallback(
    () => (
      <Typography variant="inherit" component="span">
        {deviceId}
      </Typography>
    ),
    [deviceId],
  );
  return (
    <ApolloErrorBoundary variant="section-alert" fallback={inlineError ? InlineFallback : undefined}>
      <Suspense fallback={<Fallback {...typographyProps} />}>
        <Component {...props} />
      </Suspense>
    </ApolloErrorBoundary>
  );
};

export default DeviceNameSpan;
